import React from 'react';
import {
    FinancialReport,
    FinancialReportPayload,
    financialReportService,
} from './financial-report.service';

interface Props {
    report: FinancialReport;
    onClose: () => void;
}

const formatCurrency = (value: number | null | undefined, currency?: string): string => {
    if (value === null || value === undefined) return '—';
    const fixed = Math.round(value).toLocaleString('en-US');
    return currency ? `${currency} ${fixed}` : fixed;
};

const lineRow = (label: string, value: number | null | undefined, currency?: string) => (
    <li className="h-stack jc-sb financial-report__line" key={label}>
        <span className="financial-report__label">{label}</span>
        <span className="financial-report__value">{formatCurrency(value, currency)}</span>
    </li>
);

const IncomeStatementBody: React.FC<{ payload: FinancialReportPayload }> = ({ payload }) => {
    const i = payload.incomeStatement || {};
    const c = payload.currency;
    return (
        <ul className="stack stack-xs financial-report__list">
            {lineRow('Revenue', i.revenue, c)}
            {lineRow('Cost of goods sold', i.cogs, c)}
            {lineRow('Gross profit', i.grossProfit, c)}
            {lineRow('Operating expenses', i.operatingExpenses, c)}
            {lineRow('Depreciation & amortization', i.depreciationAndAmortization, c)}
            {lineRow('EBITDA', i.ebitda, c)}
            {lineRow('Interest expense', i.interestExpense, c)}
            {lineRow('Net income', i.netIncome, c)}
        </ul>
    );
};

const BalanceSheetBody: React.FC<{ payload: FinancialReportPayload }> = ({ payload }) => {
    const a = payload.assets || {};
    const l = payload.liabilities || {};
    const e = payload.equity || {};
    const c = payload.currency;
    return (
        <div className="stack stack-s">
            <div>
                <h3 className="title title-s">Assets</h3>
                <ul className="stack stack-xs financial-report__list">
                    {lineRow('Total assets', a.total, c)}
                    {lineRow('Current assets', a.current, c)}
                    {lineRow('Non-current assets', a.nonCurrent, c)}
                    {lineRow('Cash & equivalents', a.cashAndEquivalents, c)}
                </ul>
            </div>
            <div>
                <h3 className="title title-s">Liabilities</h3>
                <ul className="stack stack-xs financial-report__list">
                    {lineRow('Total liabilities', l.total, c)}
                    {lineRow('Current liabilities', l.current, c)}
                    {lineRow('Non-current liabilities', l.nonCurrent, c)}
                    {lineRow('Short-term debt', l.shortTermDebt, c)}
                    {lineRow('Long-term debt', l.longTermDebt, c)}
                    {lineRow('Total debt', l.totalDebt, c)}
                </ul>
            </div>
            <div>
                <h3 className="title title-s">Equity</h3>
                <ul className="stack stack-xs financial-report__list">
                    {lineRow('Total equity', e.total, c)}
                </ul>
            </div>
        </div>
    );
};

export const FinancialReportModal: React.FC<Props> = ({ report, onClose }) => {
    const payload = financialReportService.parseReportPayload(report);

    const title = report.type === 'IncomeStatement' ? 'Income Statement' : 'Balance Sheet';
    const subtitle = payload?.company?.name
        ? `${payload.company.name}${payload?.fiscalYear ? ` — FY ${payload.fiscalYear}` : ''}`
        : null;

    return (
        <div className="financial-modal-overlay">
            <div className="dialog__dialog financial-report__dialog">
                <div className="stack stack-s">
                    <div className="h-stack jc-sb">
                        <div className="stack stack-xs">
                            <p className="title title-m">{title}</p>
                            {subtitle && <p className="title title-xs financial-report__subtitle">{subtitle}</p>}
                        </div>
                        <button className="icon-button dialog__close" onClick={onClose}>
                            <span className="icon-button__inner">
                                <svg className="icon icon--m">
                                    <use xlinkHref="#icon-close" />
                                </svg>
                                <span className="visually-hidden">Close</span>
                            </span>
                        </button>
                    </div>

                    {report.status === 'Pending' && (
                        <div className="financial-report__loading">
                            <p>FinnAI is collecting the filing. This usually takes 30 seconds to a few minutes.</p>
                        </div>
                    )}

                    {report.status === 'Failed' && (
                        <div className="financial-report__error">
                            <p className="bold">Report could not be generated.</p>
                            <p>{report.errorMessage || 'Unknown error.'}</p>
                            <p>Your credits were refunded.</p>
                        </div>
                    )}

                    {report.status === 'Refunded' && (
                        <div className="financial-report__error">
                            <p className="bold">Report failed — credits refunded.</p>
                            <p>{report.errorMessage || 'Unknown error.'}</p>
                        </div>
                    )}

                    {report.status === 'Completed' && payload && (
                        <div className="stack stack-s">
                            {report.type === 'IncomeStatement' ? (
                                <IncomeStatementBody payload={payload} />
                            ) : (
                                <BalanceSheetBody payload={payload} />
                            )}
                            {payload.provenance?.sourceName && (
                                <p className="financial-report__provenance">
                                    Source: {payload.provenance.sourceName}
                                    {payload.provenance.confidenceScore !== undefined && (
                                        <> · {payload.provenance.confidenceScore}% confidence</>
                                    )}
                                </p>
                            )}
                            {payload.adjustments && payload.adjustments.length > 0 && (
                                <div className="financial-report__notes">
                                    <p className="bold">Adjustments applied</p>
                                    <ul>
                                        {payload.adjustments.map((a, i) => (
                                            <li key={i}>{a}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="h-stack h-stack-xs jc-fe">
                        <button className="button button--secondary" onClick={onClose}>
                            <span className="button__inner">
                                <span>Close</span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
